import React, { useState, useRef, useMemo, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { sanitizeHTML } from '../../utils/contentSanitizer';
import { useTheme } from '../ui/theme-provider';
import { cn } from '../../lib/utils';

const BlogEditor = ({ 
  content = '', 
  onChange, 
  onRawChange,
  placeholder = "Write your blog post content...",
  readOnly = false,
  className = ""
}) => {
  const [editorContent, setEditorContent] = useState(content);
  const quillRef = useRef();
  const { theme } = useTheme();
  
  // Update editor content when content prop changes (for loading existing posts)
  useEffect(() => {
    if (content !== editorContent) {
      setEditorContent(content);
    }
  }, [content]); // Watch content prop changes

  // Memoize styles to prevent recreation on every render
  const editorStyles = useMemo(() => `
    .blog-editor .ql-editor {
      min-height: 300px !important;
      font-size: 16px !important;
      line-height: 1.6 !important;
      ${theme === 'dark' ? `
        color: #ffffff !important;
        background-color: #1a1a1a !important;
      ` : `
        color: #000000 !important;
        background-color: #ffffff !important;
      `}
    }
    
    .blog-editor .ql-toolbar {
      ${theme === 'dark' ? `
        background-color: #2a2a3e !important;
        border-color: #4a5568 !important;
        color: #ffffff !important;
      ` : `
        background-color: #ffffff !important;
        border-color: #e2e8f0 !important;
      `}
    }
    
    .blog-editor .ql-toolbar .ql-picker-label,
    .blog-editor .ql-toolbar .ql-picker-item,
    .blog-editor .ql-toolbar button {
      ${theme === 'dark' ? `
        color: #ffffff !important;
      ` : `
        color: #000000 !important;
      `}
    }
    
    .blog-editor .ql-toolbar button:hover {
      ${theme === 'dark' ? `
        background-color: #4a5568 !important;
      ` : `
        background-color: #f7fafc !important;
      `}
    }
    
    .blog-editor .ql-container {
      ${theme === 'dark' ? `
        background-color: #1a1a1a !important;
        border-color: #4a5568 !important;
      ` : `
        background-color: #ffffff !important;
        border-color: #e2e8f0 !important;
      `}
    }
    
    .blog-editor .ql-editor::before {
      ${theme === 'dark' ? `
        color: #9ca3af !important;
      ` : `
        color: #6b7280 !important;
      `}
    }
    
    .blog-editor .ql-editor h1,
    .blog-editor .ql-editor h2,
    .blog-editor .ql-editor h3,
    .blog-editor .ql-editor p,
    .blog-editor .ql-editor div {
      ${theme === 'dark' ? `
        color: #ffffff !important;
      ` : `
        color: #000000 !important;
      `}
    }
    
    .blog-editor .ql-editor blockquote {
      border-left: 4px solid ${theme === 'dark' ? '#4a5568' : '#e2e8f0'} !important;
      padding-left: 16px !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
      font-style: italic !important;
      ${theme === 'dark' ? `
        color: #d1d5db !important;
        background-color: #2d3748 !important;
      ` : `
        color: #4a5568 !important;
        background-color: #f7fafc !important;
      `}
    }
    
    .blog-editor .ql-editor code {
      ${theme === 'dark' ? `
        background-color: #2d3748 !important;
        color: #e2e8f0 !important;
      ` : `
        background-color: #f4f4f4 !important;
        color: #1a202c !important;
      `}
      padding: 2px 4px !important;
      border-radius: 3px !important;
      font-family: 'Monaco', 'Consolas', monospace !important;
    }
    
    .blog-editor .ql-editor pre {
      ${theme === 'dark' ? `
        background-color: #2d3748 !important;
        color: #e2e8f0 !important;
      ` : `
        background-color: #f4f4f4 !important;
        color: #1a202c !important;
      `}
      padding: 12px !important;
      border-radius: 4px !important;
      overflow-x: auto !important;
    }
    
    .blog-editor .ql-editor a {
      color: #7C3AED !important;
      text-decoration: underline !important;
    }
    
    .blog-editor .ql-editor a:hover {
      color: #5B21B6 !important;
    }
  `, [theme]);
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote', 'code-block'],
      ['link'],
      ['clean']
    ],
    clipboard: {
      // Strip most attributes when pasting
      matchVisual: false,
    }
  }), []);

  // Formats allowed in the editor
  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'blockquote', 'code-block',
    'link'
  ];

  const handleChange = (content, delta, source, editor) => {
    setEditorContent(content);
    
    // Store raw Quill delta for editing
    if (onRawChange) {
      onRawChange(editor.getContents());
    }
    
    // Sanitize HTML before passing to parent
    if (onChange) {
      const sanitizedHTML = sanitizeHTML(content);
      onChange(sanitizedHTML);
    }
  };

  return (
    <div className={cn("blog-editor", className)}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={editorContent}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        readOnly={readOnly}
        className={cn(
          "min-h-64",
          theme === 'dark' ? 'quill-dark-theme' : 'quill-light-theme'
        )}
      />
      
      {/* Theme-aware custom styles - memoized to prevent recreation */}
      <style>{editorStyles}</style>
    </div>
  );
};

export default BlogEditor;
